import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Home, Send, Loader } from 'lucide-react';
import axios from 'axios';

const RequestChangesPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [formData, setFormData] = useState({
    reason: 'wrong_amount',
    message: ''
  });
  
  const signedToken = searchParams.get('ref');
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    if (signedToken) {
      axios.get(`${API}/stripe/payment-info/${signedToken}`)
        .then(res => {
          setPaymentInfo(res.data);
          if (res.data.status === 'paid') {
            setError('This payment has already been completed.');
          } else if (res.data.status === 'canceled') {
            setError('This payment request has been cancelled.');
          }
        })
        .catch(err => {
          if (err.response?.status === 403) {
            setError('Invalid or expired link. Please contact support.');
          } else {
            setError('Unable to load payment information.');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setError('Invalid request. No reference provided.');
      setLoading(false);
    }
  }, [signedToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.message.length < 10) {
      alert('Please provide more details in your message (at least 10 characters).');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/stripe/change-request/${signedToken}`, formData);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reasonLabels = {
    wrong_amount: 'Incorrect Amount',
    wrong_service: 'Wrong Service Listed',
    payment_plan: 'Request Payment Plan',
    other: 'Other Issue'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F8FAFC' }}>
        <div className="text-center">
          <Loader size={48} className="animate-spin mx-auto text-[#0B1F3A] mb-4" />
          <p className="text-[#475569]">Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F8FAFC' }}>
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={32} className="text-red-600" />
          </div>
          <h1 className="font-heading text-xl font-bold text-[#0B1F3A] mb-2">Unable to Process</h1>
          <p className="text-[#475569] mb-6">{error}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white" style={{ background: '#0B1F3A' }}>
            Contact Support
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F8FAFC' }}>
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Request Submitted!</h1>
          <p className="text-[#475569] mb-6">
            Thank you for reaching out. Our team will review your request and contact you shortly.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-[#475569] border border-gray-200">
            <Home size={18} /> Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" style={{ background: '#F8FAFC' }}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="font-heading font-bold text-xl text-[#0B1F3A]">MaTaxPreparer LLC</span>
          </Link>
          <h1 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Request Changes</h1>
          <p className="text-[#475569]">Let us know if you need any modifications to your payment request.</p>
        </div>

        {/* Payment Summary */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
          <h2 className="font-semibold text-[#0B1F3A] mb-4">Payment Details</h2>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-[#94A3B8]">Client</span>
              <span className="font-medium text-[#0B1F3A]">{paymentInfo?.client_name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-[#94A3B8]">Service</span>
              <span className="font-medium text-[#0B1F3A] text-right max-w-[200px]">{paymentInfo?.service_description}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[#94A3B8]">Amount</span>
              <span className="font-bold text-xl text-[#0B1F3A]">
                ${paymentInfo?.amount?.toLocaleString()} {paymentInfo?.currency?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Change Request Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="font-semibold text-[#0B1F3A] mb-4">What would you like to change?</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-2">Reason for Change *</label>
              <select
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#0B1F3A]"
                required
              >
                {Object.entries(reasonLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#475569] mb-2">Please Explain *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#0B1F3A]"
                rows={4}
                placeholder="Please provide details about the changes you're requesting..."
                required
                minLength={10}
              />
              <p className="text-xs text-[#94A3B8] mt-1">Minimum 10 characters required</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white disabled:opacity-60"
              style={{ background: '#0B1F3A' }}
            >
              {submitting ? (
                <>
                  <Loader size={18} className="animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  <Send size={18} /> Submit Request
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-[#94A3B8] text-center mt-4">
            Our team will review your request within 1-2 business days and contact you via email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RequestChangesPage;
